/**
 * Shuttle Booking Manager
 * 
 * Main JavaScript class for managing shuttle booking data and interactions
 * 
 * @package A2A_Theme
 */

class ShuttleBookingManager {
    constructor(skipAutoInit = false) {
        this.version = this.getShuttleCacheVersion();
        this.destinations = null;
        this.availability = null;
        this.bookingData = this.loadBookingData();
        this.currentStep = parseInt(this.bookingData.step || 1);
        this.uploadsUrl = this.getUploadsUrl();
        
        // Initialize currency manager
        this.currencyManager = new ShuttleCurrencyManager();
        
        // Initialize ready promise
        this.readyPromise = new Promise((resolve) => {
            this.resolveReady = resolve;
        });
        
        // Initialize on DOM ready (unless skipAutoInit is passed)
        if (!skipAutoInit) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.init());
            } else {
                this.init();
            }
        }
    }
    
    /**
     * Initialize the booking manager
     */
    async init() {
        try {
            // Check localStorage health before proceeding
            this.checkLocalStorageHealth();
            
            // Verify cache version against server to prevent stale data
            await this.validateCacheVersion();
            
            await this.loadDestinations();
            
            // Load availability (allowed booking weekdays); fails open on error
            await this.loadAvailability();
            
            // Setup currency change listener
            this.setupCurrencyListener();
        } catch (error) {
            console.error('Failed to initialize Shuttle Booking Manager:', error);
        } finally {
            // Mark as ready regardless of success to prevent hanging
            if (this.resolveReady) {
                this.resolveReady(true);
            }
        }
    }

    /**
     * Validate and update cache version from server
     * This ensures we don't use stale version from cached HTML
     */
    async validateCacheVersion() {
        try {
            // Add timestamp to bypass browser cache for this check
            // Remove potential double slash if uploadsUrl ends with /
            const baseUrl = this.uploadsUrl.endsWith('/') ? this.uploadsUrl.slice(0, -1) : this.uploadsUrl;
            const url = `${baseUrl}/shuttle-cache/latest.json?t=${Date.now()}`;
            
            const response = await fetch(url);
            if (!response.ok) return;
            
            const data = await response.json();
            if (data && data.version && data.version !== this.version) {
                console.log('Updated shuttle cache version:', this.version, '->', data.version);
                this.version = data.version;
            }
        } catch (error) {
            console.warn('Failed to validate cache version:', error);
        }
    }
    
    /**
     * Get uploads URL for cache files
     */
    getUploadsUrl() {
        // Get the site URL and construct uploads path
        const siteUrl = window.location.origin;
        return `${siteUrl}/wp-content/uploads/`;
    }

    /**
     * Get shuttle cache version from global variable or data attribute
     */
    getShuttleCacheVersion() {
        // Try global variable first
        if (window.shuttleCacheVersion) {
            return window.shuttleCacheVersion;
        }
        
        // Try data attribute on body or main element
        const elements = [
            document.body,
            document.querySelector('[data-shuttle-cache-version]')
        ];
        
        for (const element of elements) {
            if (element && element.dataset.shuttleCacheVersion) {
                return element.dataset.shuttleCacheVersion;
            }
        }
        
        console.warn('Shuttle cache version not found');
        return '';
    }
    
    
    /**
     * Load destinations from cache
     */
    async loadDestinations() {
        if (!this.version) {
            throw new Error('No cache version available');
        }
        
        const url = `${this.uploadsUrl}/shuttle-cache/${this.version}/destinations.min.json`;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            this.destinations = await response.json();
            return this.destinations;
        } catch (error) {
            console.error('Failed to load destinations:', error);
            throw error;
        }
    }
    
    /**
     * Load availability settings (allowed booking weekdays) from cache.
     * Fails open (all days allowed) so a fetch/parse error never blocks bookings;
     * the MnG /price and /offers endpoints remain the authoritative check.
     */
    async loadAvailability() {
        // Fail-open default: all weekdays allowed (ISO 1=Mon..7=Sun)
        const failOpen = { weekdays: [1, 2, 3, 4, 5, 6, 7], timezone: null };
        
        if (!this.version) {
            this.availability = failOpen;
            return this.availability;
        }
        
        const url = `${this.uploadsUrl}/shuttle-cache/${this.version}/availability.json`;
        let result = failOpen;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            if (data && Array.isArray(data.weekdays)) {
                result = {
                    weekdays: data.weekdays,
                    timezone: data.timezone || null
                };
            }
        } catch (error) {
            console.warn('Failed to load availability settings, allowing all weekdays:', error);
        }
        
        // Assign only once resolved, so `availability` doubles as a "loaded" signal.
        // Until then getAllowedWeekdays() fails open on null.
        this.availability = result;
        return this.availability;
    }
    
    /**
     * Get allowed booking weekdays as ISO numbers (1=Mon..7=Sun).
     * Falls back to all days if availability is unavailable.
     */
    getAllowedWeekdays() {
        if (this.availability && Array.isArray(this.availability.weekdays) && this.availability.weekdays.length > 0) {
            return this.availability.weekdays;
        }
        return [1, 2, 3, 4, 5, 6, 7];
    }
    
    /**
     * Load available routes for a specific origin
     */
    async loadAvailableRoutes(fromId) {
        if (!this.version || !fromId) {
            return {};
        }
        
        const url = `${this.uploadsUrl}/shuttle-cache/${this.version}/routes-by-from/${fromId}.json`;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const routes = await response.json();
            return routes;
        } catch (error) {
            console.error(`Failed to load routes from ${fromId}:`, error);
            return {};
        }
    }
    
    /**
     * Get default booking data structure
     */
    getDefaultBookingData() {
        return {
            step: 1,
            transferType: 'return',
            from: { id: '', label: '' },
            to: { id: '', label: '' },
            outboundDate: '',
            outboundTime: '',
            returnDate: '',
            returnTime: '',
            passengers: { adults: 1, children: 0 },
            
            // Route selection data
            selectedRoutes: {
                outbound: null,
                return: null
            },
            currentSelection: 'outbound', // 'outbound' | 'return' | 'completed'
            pricePreferences: {
                tier: 'skier' // 'basic' | 'skier' | 'pro'
            },
            
            timestamp: Date.now()
        };
    }
    
    /**
     * Load booking data from localStorage
     */
    loadBookingData() {
        try {
            const stored = localStorage.getItem('shuttle_booking_data');
            if (stored) {
                let data;
                
                // Try to parse JSON
                try {
                    data = JSON.parse(stored);
                } catch (parseError) {
                    console.error('Corrupted JSON in localStorage, clearing:', parseError);
                    localStorage.removeItem('shuttle_booking_data');
                    return this.getDefaultBookingData();
                }
                
                // Validate data structure
                if (!this.validateBookingData(data)) {
                    localStorage.removeItem('shuttle_booking_data');
                    return this.getDefaultBookingData();
                }
                
                // Check if data is not too old (24 hours)
                const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
                if (data.timestamp && (Date.now() - data.timestamp) < maxAge) {
                    // Sanitize loaded data to ensure consistency
                    const sanitizedData = this.sanitizeBookingData(data);
                    // Merge with defaults, but preserve the sanitized data (especially step)
                    return { ...this.getDefaultBookingData(), ...sanitizedData };
                } else {
                    localStorage.removeItem('shuttle_booking_data');
                }
            }
        } catch (error) {
            console.error('Failed to load booking data from localStorage:', error);
            
            // Clear potentially corrupted data
            try {
                localStorage.removeItem('shuttle_booking_data');
            } catch (clearError) {
                console.error('Failed to clear corrupted data:', clearError);
            }
        }
        
        return this.getDefaultBookingData();
    }
    
    /**
     * Save booking data to localStorage
     */
    saveBookingData() {
        try {
            // Validate data before saving
            if (!this.validateBookingData(this.bookingData)) {
                console.error('Invalid booking data, not saving to localStorage');
                return false;
            }
            
            // Clean data to prevent circular references
            const cleanData = this.sanitizeBookingData(this.bookingData);
            cleanData.timestamp = Date.now();
            
            // Test JSON serialization
            const jsonString = JSON.stringify(cleanData);
            
            // Check localStorage quota
            if (jsonString.length > 5000000) { // 5MB limit
                console.error('Booking data too large for localStorage');
                return false;
            }
            
            localStorage.setItem('shuttle_booking_data', jsonString);
            return true;
        } catch (error) {
            console.error('Failed to save booking data to localStorage:', error);
            
            // Try to clear corrupted data
            try {
                localStorage.removeItem('shuttle_booking_data');
            } catch (clearError) {
                console.error('Failed to clear corrupted data:', clearError);
            }
            
            return false;
        }
    }
    
    /**
     * Update booking data
     */
    updateBookingData(updates) {
        this.bookingData = { ...this.bookingData, ...updates };
        this.saveBookingData();
    }
    
    /**
     * Set referrer page for back button navigation
     */
    setReferrerPage(url = null) {
        const referrerUrl = url || document.referrer || window.location.pathname;
        localStorage.setItem('shuttle_referrer_page', referrerUrl);
    }
    
    /**
     * Get referrer page for back button navigation
     */
    getReferrerPage() {
        const defaultUrl = window.shuttleBookingUrl || '/shared-economy-booking/';
        return localStorage.getItem('shuttle_referrer_page') || defaultUrl;
    }
    
    /**
     * Navigate back to referrer page
     */
    navigateBack() {
        const referrerPage = this.getReferrerPage();
        window.location.href = referrerPage;
    }
    
    /**
     * Clear booking data
     */
    clearBookingData() {
        try {
            localStorage.removeItem('shuttle_booking_data');
            this.bookingData = this.getDefaultBookingData();
        } catch (error) {
            console.error('Failed to clear booking data:', error);
        }
    }
    
    /**
     * Fetch available routes for a specific route and date
     * Always fetches fresh data from cache system
     */
    async fetchAvailableRoutes(fromId, toId, date) {
        try {
            // Use existing cache system - always fetch fresh data
            const routesUrl = `${this.uploadsUrl}/shuttle-cache/${this.version}/routes-by-from/${fromId}.json`;
            
            const response = await fetch(routesUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch routes: ${response.status}`);
            }
            
            const routesData = await response.json();
            
            
            // Check if destination exists in routes
            if (!routesData[toId]) {
                console.warn(`No routes found from ${fromId} to ${toId}`);
                return [];
            }
            
            // Process and return routes for the specific destination
            return await this.processRoutes(routesData[toId], date, fromId, toId);
            
        } catch (error) {
            console.error('Failed to fetch available routes:', error);
            throw error;
        }
    }
    
    /**
     * Fetch travel time data for a specific route and date
     */
    async fetchTravelTimeData(fromId, toId, date) {
        try {
            // Convert date format from DD.MM.YY to YYYY-MM-DD for API
            const apiDate = this.convertDateForAPI(date);
            
            const formData = new FormData();
            formData.append('action', 'get_travel_time');
            formData.append('from', fromId);
            formData.append('to', toId);
            formData.append('date', apiDate);
            formData.append('nonce', window.shuttleTravelTimeNonce || '');
            
            const response = await fetch(window.ajaxurl, {
                method: 'POST',
                body: formData
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(result.data?.message || 'Failed to fetch travel time');
            }
            
            return result.data.intervals || [];
            
        } catch (error) {
            console.warn('Failed to fetch travel time data:', error);
            return []; // Return empty array to fallback to default duration
        }
    }
    
    /**
     * Convert date from DD.MM.YY format to YYYY-MM-DD format
     */
    convertDateForAPI(dateStr) {
        try {
            if (!dateStr) {
                throw new Error('Empty date string');
            }
            
            // Handle format "25.09.25, 09:15" - extract just the date part
            let datePart = dateStr;
            if (dateStr.includes(',')) {
                datePart = dateStr.split(',')[0].trim(); // "25.09.25"
            }
            
            // Parse DD.MM.YY format
            const parts = datePart.split('.');
            if (parts.length !== 3) {
                throw new Error(`Invalid date format: ${datePart}`);
            }
            
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            let year = parts[2];
            
            // Convert 2-digit year to 4-digit year
            if (year.length === 2) {
                const currentYear = new Date().getFullYear();
                const currentCentury = Math.floor(currentYear / 100) * 100;
                year = currentCentury + parseInt(year, 10);
                
                // If the year is more than 50 years in the past, assume next century
                if (year < currentYear - 50) {
                    year += 100;
                }
            } else if (year.length === 4) {
                // Already 4-digit year
                year = parseInt(year, 10);
            } else {
                throw new Error(`Invalid year format: ${year}`);
            }
            
            return `${year}-${month}-${day}`;
            
        } catch (error) {
            console.warn('Failed to convert date format:', dateStr, error);
            // Fallback to current date
            const now = new Date();
            return now.toISOString().split('T')[0];
        }
    }
    
    /**
     * Find travel time for specific departure time from intervals
     */
    findTravelTimeForDeparture(intervals, departureTime) {
        if (!intervals || intervals.length === 0) {
            return null; // No travel time data available
        }
        
        try {
            const depMinutes = this.timeToMinutes(departureTime);
            
            for (const interval of intervals) {
                const fromMinutes = this.timeToMinutes(interval.time_from);
                const toMinutes = this.timeToMinutes(interval.time_to);
                
                if (depMinutes >= fromMinutes && depMinutes <= toMinutes) {
                    return {
                        travel_time: interval.travel_time,
                        duration: interval.duration
                    };
                }
            }
            
            // If no interval matches, use the first one as fallback
            if (intervals.length > 0) {
                return {
                    travel_time: intervals[0].travel_time,
                    duration: intervals[0].duration
                };
            }
            
        } catch (error) {
            console.warn('Error finding travel time for departure:', error);
        }
        
        return null;
    }
    
    /**
     * Convert time string (HH:MM:SS or HH:MM) to minutes
     */
    timeToMinutes(timeStr) {
        const parts = timeStr.split(':');
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        return hours * 60 + minutes;
    }
    
    /**
     * Convert minutes to duration string (e.g., "~1h 30m")
     */
    minutesToDurationString(totalMinutes) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        
        if (hours === 0) {
            return `~${minutes}m`;
        } else if (minutes === 0) {
            return `~${hours}h`;
        } else {
            return `~${hours}h ${minutes}m`;
        }
    }

    /**
     * Process raw route data into formatted route objects
     */
    async processRoutes(routeData, date, fromId, toId) {
        try {
            if (!routeData || !routeData.times || !Array.isArray(routeData.times)) {
                console.warn('Invalid route data structure:', routeData);
                return [];
            }
            
            // Get destination labels for display
            const fromLabel = this.getDestinationLabel(fromId);
            const toLabel = this.getDestinationLabel(toId);
            
            // Fetch travel time data for this route and date
            const travelTimeIntervals = await this.fetchTravelTimeData(fromId, toId, date);
            
            // Extract price based on date and tier
            const { priceAmount, currency, tiers } = this.extractPriceForDate(routeData.prices, date);
            
            const routes = routeData.times.map((time, index) => {
                // Validate time format
                if (!time || typeof time !== 'string') {
                    console.warn('Invalid time format:', time);
                    return null;
                }
                
                // Get travel time for this specific departure time
                const travelTimeData = this.findTravelTimeForDeparture(travelTimeIntervals, time);
                
                let duration, arrivalTime;
                
                if (travelTimeData) {
                    // Use API travel time data
                    duration = this.minutesToDurationString(travelTimeData.duration);
                    arrivalTime = this.calculateArrivalTimeFromMinutes(time, travelTimeData.duration);
                } else {
                    // Fallback to default duration
                    duration = '~1h 30m';
                    arrivalTime = this.calculateArrivalTime(time, duration);
                }
                
                // Convert price to user's selected currency
                const convertedPrice = this.currencyManager.convertPrice(priceAmount, currency);
                const formattedPrice = this.currencyManager.formatPrice(convertedPrice);
                
                return {
                    id: `route-${fromId}-${toId}-${index}`,
                    departureTime: time,
                    arrivalTime: arrivalTime,
                    duration: duration,
                    fromLocation: fromLabel,
                    toLocation: toLabel,
                    price: {
                        amount: convertedPrice,
                        currency: this.currencyManager.currentCurrency,
                        formatted: formattedPrice,
                        original: {
                            amount: priceAmount,
                            currency: currency
                        },
                        // Real per-tier EUR prices for this date (basic/skier/pro)
                        tiers: tiers || {}
                    },
                    available: true,
                    date: date,
                    travelTimeSource: travelTimeData ? 'api' : 'default' // For debugging
                };
            }).filter(route => route !== null); // Remove invalid routes
            
            return routes;
            
        } catch (error) {
            console.error('Failed to process routes:', error);
            return [];
        }
    }
    
    /**
     * Calculate arrival time based on departure time and duration in minutes
     */
    calculateArrivalTimeFromMinutes(departureTime, durationMinutes) {
        try {
            // Parse departure time (format: "HH:MM")
            const timeParts = departureTime.split(':');
            if (timeParts.length !== 2) {
                console.warn('Invalid departure time format:', departureTime);
                return departureTime;
            }
            
            const [hours, minutes] = timeParts.map(Number);
            if (isNaN(hours) || isNaN(minutes)) {
                console.warn('Invalid departure time values:', departureTime);
                return departureTime;
            }
            
            // Calculate arrival time
            const departureDate = new Date();
            departureDate.setHours(hours, minutes, 0, 0);
            
            const arrivalDate = new Date(departureDate);
            arrivalDate.setMinutes(arrivalDate.getMinutes() + durationMinutes);
            
            // Format as HH:MM
            const arrivalHours = String(arrivalDate.getHours()).padStart(2, '0');
            const arrivalMinutes = String(arrivalDate.getMinutes()).padStart(2, '0');
            
            return `${arrivalHours}:${arrivalMinutes}`;
            
        } catch (error) {
            console.warn('Error calculating arrival time from minutes:', error);
            return departureTime; // Fallback to departure time
        }
    }

    /**
     * Calculate arrival time based on departure time and duration
     */
    calculateArrivalTime(departureTime, duration) {
        try {
            // Validate inputs
            if (!departureTime || !duration) {
                console.warn('Missing departure time or duration:', { departureTime, duration });
                return departureTime || 'N/A';
            }
            
            // Parse departure time (format: "HH:MM")
            const timeParts = departureTime.split(':');
            if (timeParts.length !== 2) {
                console.warn('Invalid departure time format:', departureTime);
                return departureTime;
            }
            
            const [hours, minutes] = timeParts.map(Number);
            if (isNaN(hours) || isNaN(minutes)) {
                console.warn('Invalid departure time values:', departureTime);
                return departureTime;
            }
            
            // Parse duration (format: "~1h 40m" or "1h 40m")
            const durationMatch = duration.match(/(\d+)h\s*(\d+)m/);
            if (!durationMatch) {
                console.warn('Invalid duration format:', duration);
                return departureTime; // Fallback to departure time
            }
            
            const durationHours = parseInt(durationMatch[1], 10);
            const durationMinutes = parseInt(durationMatch[2], 10);
            
            if (isNaN(durationHours) || isNaN(durationMinutes)) {
                console.warn('Invalid duration values:', duration);
                return departureTime;
            }
            
            // Calculate arrival time
            const departureDate = new Date();
            departureDate.setHours(hours, minutes, 0, 0);
            
            const arrivalDate = new Date(departureDate);
            arrivalDate.setHours(arrivalDate.getHours() + durationHours);
            arrivalDate.setMinutes(arrivalDate.getMinutes() + durationMinutes);
            
            // Format as HH:MM
            return arrivalDate.toTimeString().slice(0, 5);
            
        } catch (error) {
            console.error('Failed to calculate arrival time:', error, 'for:', { departureTime, duration });
            return departureTime || 'N/A'; // Fallback
        }
    }
    
    /**
     * Extract price for a specific date from price tiers
     */
    extractPriceForDate(prices, dateStr) {
        try {
            // Default values
            let priceAmount = 0;
            let currency = 'EUR';
            // Per-tier prices for the specific date (EUR amounts keyed by tier)
            const tiers = {};
            
            if (!prices || typeof prices !== 'object') {
                return { priceAmount, currency, tiers };
            }
            
            // Get day of week from date (1=Monday, 7=Sunday)
            const dayOfWeek = this.getDayOfWeekFromDate(dateStr);
            const priceKey = `price_${dayOfWeek}`;
            
            // Extract price for every available tier: basic, skier, pro
            const tierOrder = ['basic', 'skier', 'pro'];
            
            for (const tier of tierOrder) {
                const tierPrices = prices[tier];
                if (tierPrices && typeof tierPrices === 'object') {
                    // Get currency from tier
                    if (tierPrices.currency) {
                        currency = tierPrices.currency;
                    }
                    
                    // Get price for the specific day
                    const dayPrice = tierPrices[priceKey];
                    let parsedDayPrice = null;
                    if (typeof dayPrice === 'number' && !isNaN(dayPrice)) {
                        parsedDayPrice = dayPrice;
                    } else if (typeof dayPrice === 'string') {
                        const parsed = parseFloat(dayPrice);
                        if (!isNaN(parsed)) {
                            parsedDayPrice = parsed;
                        }
                    }
                    
                    if (parsedDayPrice !== null) {
                        tiers[tier] = parsedDayPrice;
                        // Base "from" price stays the basic tier (first available preferred)
                        if (priceAmount === 0) {
                            priceAmount = parsedDayPrice;
                        }
                    }
                }
            }
            
            
            return { priceAmount, currency, tiers };
            
        } catch (error) {
            console.error('Failed to extract price for date:', error);
            return { priceAmount: 0, currency: 'EUR', tiers: {} };
        }
    }
    
    /**
     * Get the real fare-tier upgrade delta (per passenger, in EUR) for a route.
     *
     * The delta is the difference between the selected tier's real price and the
     * basic tier's real price, both sourced from the /schedule API data carried on
     * route.price.tiers. If the real tier data is missing for this route, falls back
     * to the legacy hardcoded delta (+10 skier / +20 pro) for that route only.
     *
     * @param {object} route - Selected route object (bookingData.selectedRoutes[dir])
     * @param {string} tier - Fare tier: 'basic' | 'skier' | 'pro'
     * @returns {number} Upgrade delta over basic, per passenger, in EUR
     */
    getTierDeltaEUR(route, tier) {
        const hardcodedDelta = { basic: 0, skier: 10, pro: 20 };
        
        if (!tier || tier === 'basic') {
            return 0;
        }
        
        const tiers = route && route.price && route.price.tiers ? route.price.tiers : null;
        
        if (tiers && typeof tiers === 'object') {
            const tierPrice = tiers[tier];
            const basicPrice = tiers.basic;
            
            if (typeof tierPrice === 'number' && !isNaN(tierPrice) &&
                typeof basicPrice === 'number' && !isNaN(basicPrice)) {
                const delta = tierPrice - basicPrice;
                return delta > 0 ? delta : 0;
            }
        }
        
        // Fallback: real tier data missing for this route - use legacy hardcoded delta
        return hardcodedDelta[tier] || 0;
    }
    
    /**
     * Get the admin-configured return-transfer discount percentage (0-100).
     * Controlled from Tools → Shuttle Transfers; each shuttle page template
     * exposes it as window.shuttleReturnDiscountPercent. Falls back to 20%.
     *
     * @returns {number} Discount percentage (e.g. 20 for 20%)
     */
    getReturnDiscountPercent() {
        const percent = (typeof window.shuttleReturnDiscountPercent === 'number' && !isNaN(window.shuttleReturnDiscountPercent))
            ? window.shuttleReturnDiscountPercent
            : 20;
        return Math.max(0, Math.min(100, percent));
    }
    
    /**
     * Get the return-transfer discount rate as a decimal (e.g. 0.20 for 20%).
     * @returns {number} Discount rate between 0 and 1
     */
    getReturnDiscountRate() {
        return this.getReturnDiscountPercent() / 100;
    }
    
    /**
     * Get day of week from date string (1=Monday, 7=Sunday)
     */
    getDayOfWeekFromDate(dateStr) {
        try {
            if (!dateStr) return 1; // Default to Monday
            
            // Handle format "25.09.25, 09:15" - extract just the date part
            let datePart = dateStr;
            if (dateStr.includes(',')) {
                datePart = dateStr.split(',')[0].trim();
            }
            
            let year, month, day;
            
            // Check for dot format (DD.MM.YY or DD.MM.YYYY)
            if (datePart.includes('.')) {
                const parts = datePart.split('.');
                if (parts.length === 3) {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    let yearStr = parts[2];
                    
                    if (yearStr.length === 2) {
                        year = parseInt(`20${yearStr}`, 10);
                    } else {
                        year = parseInt(yearStr, 10);
                    }
                }
            } 
            // Check for dash format (YYYY-MM-DD)
            else if (datePart.includes('-')) {
                const parts = datePart.split('-');
                if (parts.length === 3) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    day = parseInt(parts[2], 10);
                }
            }
            
            // If we successfully parsed the components, construct Date using local time
            // IMPORTANT: Using new Date(y, m, d) uses local time, preventing timezone shifts
            // compared to new Date("YYYY-MM-DD") which uses UTC
            let date;
            if (year && month && day) {
                // Month is 0-indexed in JS Date constructor
                date = new Date(year, month - 1, day);
            } else {
                // Fallback for unknown formats (risky for timezones)
                console.warn('Unknown date format, using fallback parsing:', dateStr);
                date = new Date(dateStr);
            }
            
            if (!date || isNaN(date.getTime())) {
                console.warn('Invalid date format, using Monday as default:', dateStr);
                return 1; // Default to Monday
            }
            
            // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
            // Convert to: 1=Monday, 2=Tuesday, ..., 7=Sunday
            const jsDay = date.getDay();
            const dayOfWeek = jsDay === 0 ? 7 : jsDay; // Convert Sunday from 0 to 7
            
            return dayOfWeek;
            
        } catch (error) {
            console.error('Failed to get day of week:', error);
            return 1; // Default to Monday
        }
    }
    
    /**
     * Get destination label by ID
     */
    getDestinationLabel(destinationId) {
        try {
            if (!this.destinations) {
                return destinationId; // Fallback to ID
            }
            
            // Search in airports
            const airport = this.destinations.airports?.find(a => a.id === destinationId);
            if (airport) {
                return airport.name || airport.label || airport.title || destinationId;
            }
            
            // Search in resorts
            const resort = this.destinations.resorts?.find(r => r.id === destinationId);
            if (resort) {
                return resort.name || resort.label || resort.title || destinationId;
            }
            
            return destinationId; // Fallback to ID
            
        } catch (error) {
            console.error('Failed to get destination label:', error);
            return destinationId;
        }
    }
    
    /**
     * Validate booking data structure
     */
    validateBookingData(data) {
        if (!data || typeof data !== 'object') {
            return false;
        }
        
        // Check required properties
        const requiredProps = ['step', 'transferType', 'from', 'to', 'passengers'];
        for (const prop of requiredProps) {
            if (!(prop in data)) {
                console.error(`Missing required property: ${prop}`);
                return false;
            }
        }
        
        // Validate specific structures
        if (!data.from || typeof data.from !== 'object' || !('id' in data.from) || !('label' in data.from)) {
            console.error('Invalid from structure');
            return false;
        }
        
        if (!data.to || typeof data.to !== 'object' || !('id' in data.to) || !('label' in data.to)) {
            console.error('Invalid to structure');
            return false;
        }
        
        if (!data.passengers || typeof data.passengers !== 'object' || 
            !('adults' in data.passengers) || !('children' in data.passengers)) {
            console.error('Invalid passengers structure');
            return false;
        }
        
        return true;
    }
    
    /**
     * Sanitize booking data to prevent circular references and invalid data
     */
    sanitizeBookingData(data) {
        const sanitized = {
            step: parseInt(data.step) || 1,
            transferType: String(data.transferType || 'return'),
            from: {
                id: String(data.from?.id || ''),
                label: String(data.from?.label || '')
            },
            to: {
                id: String(data.to?.id || ''),
                label: String(data.to?.label || '')
            },
            outboundDate: String(data.outboundDate || ''),
            outboundTime: String(data.outboundTime || ''),
            returnDate: String(data.returnDate || ''),
            returnTime: String(data.returnTime || ''),
            passengers: {
                adults: parseInt(data.passengers?.adults) || 1,
                children: parseInt(data.passengers?.children) || 0
            },
            
            // Route selection data
            selectedRoutes: {
                outbound: data.selectedRoutes?.outbound || null,
                return: data.selectedRoutes?.return || null
            },
            currentSelection: String(data.currentSelection || 'outbound'),
            pricePreferences: {
                tier: String(data.pricePreferences?.tier || 'skier')
            }
        };
        
        // Preserve checkout-specific data (luggage, child seats, bus stop)
        if (data.luggage) {
            sanitized.luggage = {
                additional_bag: parseInt(data.luggage.additional_bag) || 0,
                ski_bag: parseInt(data.luggage.ski_bag) || 0
            };
        }
        
        if (data.childSeats) {
            sanitized.childSeats = {
                regular: parseInt(data.childSeats.regular) || 0,
                booster: parseInt(data.childSeats.booster) || 0
            };
        }
        
        if (data.selectedBusStop) {
            sanitized.selectedBusStop = String(data.selectedBusStop);
        }
        
        return sanitized;
    }
    
    /**
     * Check localStorage health and clean up if necessary
     */
    checkLocalStorageHealth() {
        try {
            // Test localStorage availability
            const testKey = 'shuttle_test_' + Date.now();
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            
            // Check for any corrupted shuttle data
            const keys = Object.keys(localStorage);
            const shuttleKeys = keys.filter(key => key.startsWith('shuttle_'));
            
            for (const key of shuttleKeys) {
                try {
                    const value = localStorage.getItem(key);
                    if (value) {
                        // Skip JSON parsing for keys that store plain strings
                        if (key === 'shuttle_referrer_page') {
                            // Just validate it's a reasonable URL-like string
                            if (typeof value !== 'string' || value.length > 500) {
                                throw new Error('Invalid referrer page format');
                            }
                        } else {
                            JSON.parse(value); // Test if it's valid JSON for other keys
                        }
                    }
                } catch (error) {
                    console.warn(`Removing corrupted localStorage key: ${key}`, error);
                    localStorage.removeItem(key);
                }
            }
            
        } catch (error) {
            console.error('localStorage health check failed:', error);
            
            // If localStorage is completely broken, try to clear shuttle data
            try {
                const keys = Object.keys(localStorage);
                const shuttleKeys = keys.filter(key => key.startsWith('shuttle_'));
                shuttleKeys.forEach(key => {
                    try {
                        localStorage.removeItem(key);
                    } catch (e) {
                        console.error(`Failed to remove key ${key}:`, e);
                    }
                });
            } catch (clearError) {
                console.error('Failed to clear corrupted localStorage:', clearError);
            }
        }
    }
    
    /**
     * Get language prefix from current URL (for Weglot support)
     * @returns {string} Language prefix like '/fr' or empty string
     */
    getLanguagePrefix() {
        const pathname = window.location.pathname;
        // Match language codes like /fr/, /es/, /de/, etc. at the start of the path
        const match = pathname.match(/^\/([a-z]{2})\//);
        return match ? `/${match[1]}` : '';
    }
    
    /**
     * Build URL with language prefix support
     * @param {string} url - The URL (can be absolute or relative)
     * @returns {string} Full URL with language prefix if applicable
     */
    buildLanguageAwareUrl(url) {
        if (!url) return url;
        
        const langPrefix = this.getLanguagePrefix();
        
        // If no language prefix detected, return URL as-is
        if (!langPrefix) return url;
        
        // If URL is absolute (starts with http:// or https://), extract the path
        if (url.startsWith('http://') || url.startsWith('https://')) {
            try {
                const urlObj = new URL(url);
                const pathname = urlObj.pathname;
                
                // WEGLOT FIX: Check if pathname already has ANY language prefix (not just current one)
                // This prevents double-prefixing like /ru/fr/
                if (pathname.match(/^\/[a-z]{2}\//)) {
                    return url; // Already has a language prefix, don't modify
                }
                
                // Add current language prefix to pathname
                urlObj.pathname = `${langPrefix}${pathname}`;
                return urlObj.toString();
            } catch (e) {
                console.error('Invalid URL:', url);
                return url;
            }
        }
        
        // For relative URLs
        // WEGLOT FIX: Check if it already has ANY language prefix
        if (url.match(/^\/[a-z]{2}\//)) {
            return url; // Already has a language prefix, don't modify
        }
        
        // Ensure path starts with /
        const cleanPath = url.startsWith('/') ? url : `/${url}`;
        return `${langPrefix}${cleanPath}`;
    }
    
    /**
     * Navigate to a specific step
     */
    async goToStep(stepNumber) {
        try {
            if (stepNumber === 1) {
                window.location.href = this.buildLanguageAwareUrl('/shuttle-transfers/');
            } else if (stepNumber === 2) {
                // Set current page as referrer before navigating to step 2
                this.setReferrerPage(window.location.href);
                const bookingUrl = window.shuttleBookingUrl || '/shared-economy-booking/';
                window.location.href = this.buildLanguageAwareUrl(bookingUrl);
            } else {
                // For future steps (3, 4, etc.) - will be updated when those pages are created
                this.setReferrerPage(window.location.href);
                const bookingUrl = window.shuttleBookingUrl || '/shared-economy-booking/';
                window.location.href = this.buildLanguageAwareUrl(`${bookingUrl}?step=${stepNumber}`);
            }
        } catch (error) {
            console.error(`Failed to navigate to step ${stepNumber}:`, error);
        }
    }
    
    /**
     * Get all destinations
     */
    getDestinations() {
        return this.destinations;
    }
    
    /**
     * Get destinations by type (airports or resorts)
     */
    getDestinationsByType(type) {
        if (!this.destinations || !this.destinations[type]) {
            return [];
        }
        
        return this.destinations[type];
    }
    
    /**
     * Search destinations by query
     */
    searchDestinations(query, type = null) {
        if (!query || !this.destinations) {
            return [];
        }
        
        const searchQuery = query.toLowerCase().trim();
        const results = [];
        
        const typesToSearch = type ? [type] : ['airports', 'resorts'];
        
        for (const destType of typesToSearch) {
            if (this.destinations[destType]) {
                for (const destination of this.destinations[destType]) {
                    // Search in label and tokens
                    const matchesLabel = destination.label.toLowerCase().includes(searchQuery);
                    const matchesTokens = destination.tokens.some(token => 
                        token.toLowerCase().includes(searchQuery)
                    );
                    
                    if (matchesLabel || matchesTokens) {
                        results.push({
                            ...destination,
                            type: destType
                        });
                    }
                }
            }
        }
        
        // Sort by relevance (exact matches first, then partial matches)
        results.sort((a, b) => {
            const aExact = a.label.toLowerCase() === searchQuery;
            const bExact = b.label.toLowerCase() === searchQuery;
            
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            
            return a.label.localeCompare(b.label);
        });
        
        return results;
    }
    
    /**
     * Format passenger count for display
     */
    formatPassengerCount() {
        const { adults, children } = this.bookingData.passengers;
        const total = adults + children;
        
        if (total === 1) {
            return '1';
        }
        
        const parts = [];
        if (adults > 0) parts.push(`${adults} adult${adults > 1 ? 's' : ''}`);
        if (children > 0) parts.push(`${children} child${children > 1 ? 'ren' : ''}`);
        
        return parts.join(', ');
    }
    
    /**
     * Save selected route for checkout
     */
    saveSelectedRoute(routeData, routeType = 'outbound') {
        try {
            // Validate route data
            if (!routeData || !routeData.id) {
                throw new Error('Invalid route data provided');
            }
            
            // Create route selection object (minimal data for API calls)
            // IMPORTANT: Always use original EUR price from cache, not the converted display price
            const originalPrice = routeData.price?.original || {
                amount: routeData.price?.amount || 0,
                currency: routeData.price?.currency || 'EUR'
            };
            
            const routeSelection = {
                id: routeData.id,
                departureTime: routeData.departureTime,
                price: {
                    amount: originalPrice.amount, // Use original EUR price, not converted
                    currency: originalPrice.currency, // Should always be 'EUR'
                    formatted: routeData.price?.formatted || '',
                    original: originalPrice,
                    tier: this.bookingData.pricePreferences?.tier || 'basic',
                    // Real per-tier EUR prices for this route/date (basic/skier/pro)
                    tiers: routeData.price?.tiers || {}
                },
                // Store route metadata for API calls
                routeData: {
                    fromId: this.bookingData.from.id,
                    toId: this.bookingData.to.id,
                    date: routeType === 'outbound' ? this.bookingData.outboundDate : this.bookingData.returnDate
                },
                timestamp: Date.now()
            };
            
            // Save to booking data
            this.bookingData.selectedRoutes[routeType] = routeSelection;
            
            // Update current selection state with smart logic for re-selections
            if (routeType === 'outbound' && this.bookingData.transferType === 'return') {
                // For return trips, after selecting outbound, we need return selection
                this.bookingData.currentSelection = 'return';
            } else if (routeType === 'return' && this.bookingData.transferType === 'return') {
                // For return trips, after selecting return, we're completed
                this.bookingData.currentSelection = 'completed';
            } else {
                // One-way trips are completed after outbound selection
                this.bookingData.currentSelection = 'completed';
            }
            
            // Save to localStorage
            this.saveBookingData();
            
            return true;
            
        } catch (error) {
            console.error('Failed to save selected route:', error);
            return false;
        }
    }
    
    /**
     * Get selected route for a specific type
     */
    getSelectedRoute(routeType = 'outbound') {
        return this.bookingData.selectedRoutes?.[routeType] || null;
    }
    
    /**
     * Check if ready to proceed to checkout
     */
    isReadyForCheckout() {
        const { transferType, selectedRoutes } = this.bookingData;
        
        // One-way: need outbound route
        if (transferType === 'one-way') {
            return selectedRoutes.outbound !== null;
        }
        
        // Return: need both outbound and return routes
        if (transferType === 'return') {
            return selectedRoutes.outbound !== null && selectedRoutes.return !== null;
        }
        
        return false;
    }
    
    /**
     * Check if route selections are still valid for current booking parameters
     */
    validateRouteSelections() {
        const data = this.bookingData;
        
        // If no route selections exist, nothing to validate
        if (!data.selectedRoutes?.outbound && !data.selectedRoutes?.return) {
            return true;
        }
        
        // Check if outbound route is still valid
        if (data.selectedRoutes?.outbound) {
            const outbound = data.selectedRoutes.outbound;
            
            // Validate route matches current form parameters
            if (outbound.fromId !== data.from?.id || 
                outbound.toId !== data.to?.id ||
                outbound.date !== data.outboundDate ||
                outbound.time !== data.outboundTime) {
                
                console.warn('Outbound route selection is invalid for current parameters, resetting selections');
                return false;
            }
        }
        
        // Check if return route is still valid (if exists)
        if (data.selectedRoutes?.return) {
            const returnRoute = data.selectedRoutes.return;
            
            // For return routes, from/to are swapped and we use return date/time
            if (returnRoute.fromId !== data.to?.id || 
                returnRoute.toId !== data.from?.id ||
                returnRoute.date !== data.returnDate ||
                returnRoute.time !== data.returnTime) {
                
                console.warn('Return route selection is invalid for current parameters, resetting selections');
                return false;
            }
        }
        
        return true;
    }
    
    /**
     * Reset route selections and current selection state
     */
    resetRouteSelections() {
        this.updateBookingData({
            selectedRoutes: {
                outbound: null,
                return: null
            },
            currentSelection: 'outbound'
        });
        
        console.warn('Route selections reset due to parameter changes');
    }
    
    /**
     * Validate booking data for current step
     */
    validateStep(step = this.currentStep) {
        const errors = [];
        
        // Load translatable strings for error messages
        const translatableStrings = this.loadTranslatableStrings();
        
        switch (step) {
            case 1:
                if (!this.bookingData.from.id) {
                    errors.push(translatableStrings?.errorDepartureRequired || 'Please select departure location');
                }
                if (!this.bookingData.to.id) {
                    errors.push(translatableStrings?.errorDestinationRequired || 'Please select destination');
                }
                if (!this.bookingData.outboundDate) {
                    errors.push(translatableStrings?.errorOutboundDateRequired || 'Please select outbound date and time');
                }
                if (this.bookingData.transferType === 'return' && !this.bookingData.returnDate) {
                    errors.push(translatableStrings?.errorReturnDateRequired || 'Please select return date and time');
                }
                // Guard against disallowed booking weekdays
                {
                    const allowedWeekdays = this.getAllowedWeekdays();
                    const dayNotAvailableMsg = translatableStrings?.errorDayNotAvailable || 'Shared economy transfers only run on selected days. Please choose a different date.';
                    if (this.bookingData.outboundDate && !allowedWeekdays.includes(this.getDayOfWeekFromDate(this.bookingData.outboundDate))) {
                        errors.push(dayNotAvailableMsg);
                    } else if (this.bookingData.transferType === 'return' && this.bookingData.returnDate && !allowedWeekdays.includes(this.getDayOfWeekFromDate(this.bookingData.returnDate))) {
                        errors.push(dayNotAvailableMsg);
                    }
                }
                break;
                
            // Add validation for other steps as needed
        }
        
        return errors;
    }
    
    /**
     * Load translatable strings from hidden DOM elements
     * This allows Weglot to translate them
     */
    loadTranslatableStrings() {
        const container = document.getElementById('shuttle-translatable-strings');
        if (!container) {
            return {};
        }
        
        const strings = {};
        const spans = container.querySelectorAll('span[data-key]');
        spans.forEach(span => {
            const key = span.getAttribute('data-key');
            const text = span.textContent;
            strings[key] = text;
        });
        
        return strings;
    }
    
    /**
     * Setup currency change listener
     */
    setupCurrencyListener() {
        // Listen for currency changes from header
        window.addEventListener('shuttleCurrencyChanged', async (event) => {
            await this.handleCurrencyChange(event.detail);
        });
        
    }
    
    /**
     * Handle currency change event
     */
    async handleCurrencyChange(currencyData) {
        try {
            const { currency_code, currency_symbol } = currencyData;
            
            // Update currency manager
            await this.currencyManager.updateCurrency(currency_code, currency_symbol);
            
            // Refresh current view with new currency
            await this.refreshCurrentView();
            
        } catch (error) {
            console.error('Failed to handle currency change:', error);
        }
    }
    
    /**
     * Refresh current view with new currency
     */
    async refreshCurrentView() {
        try {
            // Determine current step and refresh accordingly
            if (this.currentStep === 2) {
                // Reload and re-render routes with new currency
                const step2Handler = window.shuttleStep2Handler;
                if (step2Handler && typeof step2Handler.reloadRoutesWithNewCurrency === 'function') {
                    await step2Handler.reloadRoutesWithNewCurrency();
                }
            }
            
            // Add other steps as needed in the future
        } catch (error) {
            console.error('Failed to refresh current view:', error);
        }
    }
}

// Create global instance when DOM is ready
(function() {
    function initializeShuttleManager() {
        window.shuttleBookingManager = new ShuttleBookingManager();
    }
    
    // Try jQuery first, then fallback to vanilla JS
    if (typeof jQuery !== 'undefined') {
        jQuery(document).ready(initializeShuttleManager);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeShuttleManager);
    } else {
        // DOM is already ready
        initializeShuttleManager();
    }
})();
